import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Hook Matomo Analytics pour tracker les pages vues et événements
 * 
 * Configuration requise dans .env.local :
 * REACT_APP_MATOMO_URL=https://analytics.leagora.io
 * REACT_APP_MATOMO_SITE_ID=1
 * REACT_APP_MATOMO_ENABLED=true
 */

// Configuration Matomo depuis les variables d'environnement
const MATOMO_URL = process.env.REACT_APP_MATOMO_URL;
const MATOMO_SITE_ID = process.env.REACT_APP_MATOMO_SITE_ID;
const MATOMO_ENABLED = process.env.REACT_APP_MATOMO_ENABLED === 'true';

// Debug : afficher les variables
console.log('[Matomo Debug] Variables d\'environnement:', {
  MATOMO_URL,
  MATOMO_SITE_ID,
  MATOMO_ENABLED,
  raw_enabled: process.env.REACT_APP_MATOMO_ENABLED
});

/**
 * Initialise le tracker Matomo
 */
const initMatomo = () => {
  if (!MATOMO_ENABLED || !MATOMO_URL || !MATOMO_SITE_ID) {
    console.log('[Matomo] Analytics désactivé ou non configuré');
    console.log('[Matomo] État:', { 
      enabled: MATOMO_ENABLED, 
      hasUrl: !!MATOMO_URL, 
      hasSiteId: !!MATOMO_SITE_ID 
    });
    return false;
  }

  // Initialiser _paq si non existant
  window._paq = window._paq || [];
  
  // Configuration de base
  window._paq.push(['trackPageView']);
  window._paq.push(['enableLinkTracking']);
  window._paq.push(['setTrackerUrl', `${MATOMO_URL}/matomo.php`]);
  window._paq.push(['setSiteId', MATOMO_SITE_ID]);

  // Charger le script Matomo
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = `${MATOMO_URL}/matomo.js`;
  
  const firstScript = document.getElementsByTagName('script')[0];
  firstScript.parentNode.insertBefore(script, firstScript);

  return true;
};

/**
 * Hook pour tracker automatiquement les changements de page
 */
export const useMatomoPageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    if (!MATOMO_ENABLED || !window._paq) return;

    // Track page view avec le nouveau path
    window._paq.push(['setCustomUrl', window.location.href]);
    window._paq.push(['setDocumentTitle', document.title]);
    window._paq.push(['trackPageView']);
  }, [location]);
};

/**
 * Fonction pour tracker un événement personnalisé
 * 
 * @param {string} category - Catégorie de l'événement (ex: 'Button', 'Form', 'Video')
 * @param {string} action - Action effectuée (ex: 'Click', 'Submit', 'Play')
 * @param {string} name - Nom de l'élément (ex: 'Register CTA', 'Pricing Card')
 * @param {number} value - Valeur numérique optionnelle
 */
export const trackEvent = (category, action, name, value = null) => {
  if (!MATOMO_ENABLED || !window._paq) return;

  if (value !== null) {
    window._paq.push(['trackEvent', category, action, name, value]);
  } else {
    window._paq.push(['trackEvent', category, action, name]);
  }
};

/**
 * Fonction pour tracker une conversion de but (goal)
 * 
 * @param {number} goalId - ID du but dans Matomo
 * @param {number} revenue - Revenu optionnel associé
 */
export const trackGoal = (goalId, revenue = null) => {
  if (!MATOMO_ENABLED || !window._paq) return;

  if (revenue !== null) {
    window._paq.push(['trackGoal', goalId, revenue]);
  } else {
    window._paq.push(['trackGoal', goalId]);
  }
};

/**
 * Fonction pour tracker un téléchargement
 * 
 * @param {string} url - URL du fichier téléchargé
 */
export const trackDownload = (url) => {
  if (!MATOMO_ENABLED || !window._paq) return;
  window._paq.push(['trackLink', url, 'download']);
};

/**
 * Fonction pour tracker un clic sortant (external link)
 * 
 * @param {string} url - URL du lien externe
 */
export const trackOutlink = (url) => {
  if (!MATOMO_ENABLED || !window._paq) return;
  window._paq.push(['trackLink', url, 'link']);
};

/**
 * Composant MatomoAnalytics à placer dans App.js
 * Initialise Matomo au montage de l'application
 */
const MatomoAnalytics = ({ children }) => {
  useEffect(() => {
    const initialized = initMatomo();
    
    if (initialized) {
      console.log('Matomo Analytics initialisé');
    }
  }, []);

  // Hook pour tracker les changements de page
  useMatomoPageTracking();

  return children || null;
};

export default MatomoAnalytics;

